import type { Shop } from '../services/apiService';

// What a shop row SAYS, in one place, because two surfaces now say it.
//
// The hub table shows one status word per shop; the room shows the sentence.
// They are two projections of the same served fields — never two opinions — so
// they live together and a change to one is visibly a change to the other.

export type Tone = 'good' | 'warn' | 'bad' | 'plain';

export const toneColour: Record<Tone, string> = {
  good: 'var(--good)', warn: 'var(--warn, #8a5a00)', bad: 'var(--danger)', plain: 'var(--muted)',
};

export function hoursAgo(iso: string): string {
  const hours = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 3_600_000));
  if (hours === 0) return 'just now';
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

/**
 * A supply state the switches below do not handle is a COMPILE error. They
 * used to end in `default:` falling into the `active` branch, so a new state
 * (`repairing`) would have rendered as a green "Synced 3h ago" — wrong in the
 * worst direction, and invisible to the type checker.
 */
function unhandledSupply(state: never): never {
  throw new Error(`unhandled supply state: ${String(state)}`);
}

/**
 * THE STATE CELL — AND ON A HEALTHY ROW IT IS EMPTY.
 *
 * This said `Syncing` for every healthy URL feed: **31 of 32 rows on the live
 * account, ~94% modal**. By this screen's own element scorer — the one that sent
 * `health warning` to the room for being "constant, no information" at 79% — a
 * column that constant carries nothing, and the group heading already says
 * `Running (31)`.
 *
 * THE FIRST FIX WAS THE SYNC AGE, AND MEASURING IT KILLED IT. Per-feed cycles are
 * 24h (`next_sync_at = now() + interval '24 hours'`), so ages looked like they
 * would spread — but the scheduler works a batch every five minutes and the queue
 * drains together. Measured on the live account 2026-09-06: 29 URL feeds, **three
 * distinct hour values** — 18 at 11h, 10 at 10h, one at 146h — so the age is 62%
 * modal, and "10h ago" against "11h ago" is a distinction no publisher can act on.
 * The 146h outlier is `sofiline`, which is already in the attention group for
 * being stopped.
 *
 * So the honest answer is that a healthy account has nothing to report per row,
 * and the cell is BLANK. Absence is the signal — the same shape as a warning at
 * zero being success. The word appears only where it differs from "fine":
 *
 *   Uploaded   a file, which cannot refresh at all — the older rule, and the one
 *              that stops a frozen file reading like a feed syncing daily
 *   Syncing    genuinely mid-first-sync, which is the only time it is true
 *   Paused     the publisher's own choice
 *   Reconnecting  a managed feed WE are rebuilding (feed-repair) — deliberately
 *              NOT in the attention group, because there is nothing for them to do
 *   Stopped / Retrying / Preparing   the exceptions, all in the attention group
 *
 * When a publisher wants the age of a particular feed, it is on that feed's room,
 * which is one tap from the row.
 */
export function statusWord(shop: Shop): { text: string; tone: Tone } {
  switch (shop.supply) {
    case 'none':      return { text: 'Not added', tone: 'plain' };
    case 'preparing': return { text: 'Preparing', tone: 'plain' };
    case 'failing':   return { text: 'Retrying', tone: 'warn' };
    case 'stopped':   return { text: 'Stopped', tone: 'bad' };
    case 'paused':    return { text: 'Paused', tone: 'plain' };
    case 'repairing': return { text: 'Reconnecting', tone: 'plain' };
    case 'active':
      if (!shop.sourceUrlDisplay) return { text: 'Uploaded', tone: 'plain' };
      return shop.lastSyncedAt
        ? { text: '', tone: 'plain' }
        : { text: 'Syncing', tone: 'plain' };
    default:
      return unhandledSupply(shop.supply);
  }
}

/**
 * What the row says about itself, in the publisher's terms — the room's version.
 *
 * `stopped` and `paused` are deliberately different sentences with different
 * subjects — "we stopped" versus "you paused". They are the same two database
 * columns and telling them apart is the whole reason the server distinguishes
 * them.
 */
export function stateLine(shop: Shop): { text: string; tone: Tone } {
  switch (shop.supply) {
    case 'none':
      return { text: 'Not added', tone: 'plain' };
    case 'preparing':
      return shop.feedId !== null
        ? { text: 'Waiting for its first sync', tone: 'plain' }
        : { text: 'Fetching this shop’s products — it is new to us, so it takes a few minutes', tone: 'plain' };
    case 'failing':
      return { text: `Last sync failed — ${shop.lastSyncError || 'unknown error'}. Still retrying.`, tone: 'warn' };
    case 'stopped':
      return { text: `We stopped syncing after repeated failures — ${shop.lastSyncError || 'unknown error'}`, tone: 'bad' };
    case 'paused':
      return { text: 'You paused syncing', tone: 'plain' };
    case 'repairing':
      // WE are fixing it, and the sentence says so. The products stay on the
      // site meanwhile, which is the part a publisher worries about.
      return {
        text: 'This shop’s product export stopped updating, so we are rebuilding it. Its products stay on your site meanwhile — nothing for you to do.',
        tone: 'plain',
      };
    case 'active':
      // The AGE is stated here; the "too old" threshold lives on the server and
      // is rendered by FeedHealthBanner, so the number is stated once.
      if (!shop.sourceUrlDisplay) {
        return {
          text: shop.lastUploadAt
            ? `Uploaded ${hoursAgo(shop.lastUploadAt)} — this file will not refresh on its own`
            : 'Products uploaded from a file — it will not refresh on its own',
          tone: 'plain',
        };
      }
      if (!shop.lastSyncedAt) return { text: 'Syncing', tone: 'good' };
      return { text: `Synced ${hoursAgo(shop.lastSyncedAt)}`, tone: 'good' };
    default:
      return unhandledSupply(shop.supply);
  }
}

/** The network's numbers, in words, and only the ones it actually reported. */
export function figures(shop: Shop): string[] {
  const bits: string[] = [];
  if (shop.productCount !== null) bits.push(`${shop.productCount.toLocaleString()} products`);
  else if (shop.programmeItemCount !== null) {
    // The PROGRAMME's size, never presented as what the publisher will receive.
    bits.push(`${shop.programmeItemCount.toLocaleString()} products in this shop’s catalogue`);
  }
  if (shop.commissionRate !== null) bits.push(`${shop.commissionRate}% commission`);
  if (shop.cookieDays !== null) bits.push(`${shop.cookieDays}-day cookie`);
  const approval = shop.approvalAmountPct ?? shop.approvalCountPct;
  if (approval !== null) {
    const basis = shop.approvalAmountPct !== null ? 'of commission value' : 'of commissions';
    bits.push(`approves ${approval}% ${basis}`);
  }
  // A rank means nothing without the population it is out of, and a population
  // of zero is not one — `1 - (rank-1)/0` renders as -Infinity%.
  if (shop.epcRank !== null && shop.epcRankOf !== null && shop.epcRankOf > 0) {
    bits.push(`earns per click better than ${Math.round((1 - (shop.epcRank - 1) / shop.epcRankOf) * 100)}% of the merchants ranked`);
  } else if (shop.epcValue !== null) {
    bits.push(`${shop.epcValue} ${shop.epcCurrency ?? ''} per click`.trim());
  }
  if (shop.paymentDays !== null) bits.push(`pays in about ${shop.paymentDays} days`);
  return bits;
}

/**
 * DOES THIS SHOP NEED THE PUBLISHER?
 *
 * Two sources, and the split between them is the point:
 *
 *  - `stopped` and `failing` come from the SERVED `supply`, which the server
 *    derives from `sync_enabled` and `last_sync_error` together. The client must
 *    not re-derive it: those two columns alone cannot tell a feed the scheduler
 *    struck out from one the publisher paused, which is exactly the mistake this
 *    screen must not make.
 *
 *  - A stale upload is a SERVER-OWNED THRESHOLD (`STALE_UPLOAD_DAYS`, served by
 *    `getSetupStatus`), so its feed ids are passed in rather than computed here.
 *    This screen has been burned twice by a client holding a second copy of a
 *    threshold; the row states the age, the server owns "too old".
 *
 * `paused` is deliberately NOT attention: the publisher chose it. Reading their
 * own decision back to them as a fault is how a real alert stops being read.
 */
export function needsAttention(shop: Shop, staleFeedIds: ReadonlySet<number>): boolean {
  if (shop.supply === 'stopped' || shop.supply === 'failing') return true;
  return shop.feedId !== null && staleFeedIds.has(shop.feedId);
}
